/**
 * CUANTO ENTRO Y SALIO DE LA CUENTA COMPARTIDA PARA CADA NEGOCIO (T5, BANCO-MULTINEGOCIO-V1-001; Ares #28909). Sin DOM.
 *
 * DEFINICION (INSTR-INDICADORES-001): neto del mes por destino = suma de "valor" de los movimientos del extracto de esa
 * cuenta en el mes (entradas positivas, salidas negativas), agrupados por a quien se atribuyeron:
 *   - un negocio de la cuenta (atribuidoBusinessId),
 *   - personal / fuera (atribucionFuera),
 *   - sin atribuir (ninguno de los dos).
 * Fuente: GET /api/v1/auditoria/bank-movements (nexo-api 542). Unidad: pesos. Corte: la fecha del movimiento.
 *
 * POR QUE NETO Y NO SALDO: el extracto cargado no trae saldo por fila; un saldo necesita el saldo inicial de la cuenta
 * (el saldo rodante ya lo pide a mano). Esto dice cuanto se movio, no cuanto queda. Lo sin atribuir se muestra aparte:
 * no se reparte.
 *
 * @module application/conciliacion/netosPorNegocio
 */

const de = (o, ...k) => { for (const c of k) if (o && o[c] !== undefined && o[c] !== null) return o[c]; return null; };

/** A donde va un movimiento: id de negocio, 'fuera' o 'sin-atribuir'. */
export function destinoDe(m) {
  if (de(m, 'atribucionFuera', 'AtribucionFuera') === true) return 'fuera';
  const b = de(m, 'atribuidoBusinessId', 'AtribuidoBusinessId');
  return b ? String(b) : 'sin-atribuir';
}

/** Los meses con movimientos ('yyyy-MM'), del mas reciente al mas viejo. */
export function mesesDe(items) {
  const s = new Set((items || []).map((m) => String(de(m, 'fecha', 'Fecha') ?? '').slice(0, 7)).filter((x) => /^\d{4}-\d{2}$/.test(x)));
  return [...s].sort().reverse();
}

/**
 * Las filas del mes: una por destino con entradas, salidas (en positivo), neto y cuantos movimientos; en el orden de los
 * destinos de la cuenta, despues personal y al final sin atribuir. Los destinos sin movimientos salen en cero.
 * @param {object[]} items       la lista del GET
 * @param {string} mes          'yyyy-MM'
 * @param {{destino:string, nombre:string}[]} destinos  los de GET .../atribucion/destinos (incluye 'fuera')
 */
export function netosDelMes(items, mes, destinos = []) {
  const filas = new Map();
  const asegurar = (k, nombre) => { if (!filas.has(k)) filas.set(k, { destino: k, nombre, entradas: 0, salidas: 0, neto: 0, movimientos: 0 }); return filas.get(k); };
  for (const d of destinos) asegurar(d.destino, d.nombre);
  for (const m of items || []) {
    if (!String(de(m, 'fecha', 'Fecha') ?? '').startsWith(mes + '-')) continue;
    const v = Number(de(m, 'valor', 'Valor')) || 0;
    const k = destinoDe(m);
    const f = asegurar(k, k === 'sin-atribuir' ? 'Sin atribuir' : k === 'fuera' ? 'Personal / fuera' : k);
    if (v >= 0) f.entradas += v; else f.salidas += -v;
    f.neto += v;
    f.movimientos++;
  }
  const orden = (k) => (k === 'sin-atribuir' ? 2 : k === 'fuera' ? 1 : 0);
  const pos = new Map(destinos.map((d, i) => [d.destino, i]));
  return [...filas.values()].sort((a, b) => orden(a.destino) - orden(b.destino) || (pos.get(a.destino) ?? 99) - (pos.get(b.destino) ?? 99));
}

/** El total de la cuenta en el mes: la suma de todos los netos (atribuidos o no). */
export function totalDelMes(filas) {
  return (filas || []).reduce((t, f) => ({ entradas: t.entradas + f.entradas, salidas: t.salidas + f.salidas, neto: t.neto + f.neto, movimientos: t.movimientos + f.movimientos }),
    { entradas: 0, salidas: 0, neto: 0, movimientos: 0 });
}
